import { useState, useEffect } from "react";

// Third-Party Libraries
import axios from "axios";

// MUI Components
import { IconButton, Snackbar, Tooltip, Menu, MenuItem } from "@mui/material";

// Ant Design Components
import { Image, message } from "antd";

// MUI Icons
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import DomainAddIcon from "@mui/icons-material/DomainAdd";
import ModeIcon from "@mui/icons-material/Mode";

// Utils
import {
  formatSpecialText,
  handleActionOneUnit,
  handleActionOneUnitNewSpecial,
} from "../../utils/unitcopy.utils";
import useAxiosInstance from "../../helpers/useAxiosInstance";
import { processSpecials, specialOrder } from "../../utils/specials.utils";
import AmenitiesToggle from "../custom/AmenitiesToggle";

const TabProperties = ({
  propertyDetails,
  currentRow,
  current,
  currentUnit,
  onData,
  virtual,
  localLink,
  driveData,
  selectedClient = null,
  handleUpdateUnit,
  updateVirtualTourLink = () => {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const [currentVirtualModal, setCurrentVirtualModal] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editModeForNewSpecial, setEditModeForNewSpecial] = useState(false);
  const [disabledSwitches, setDisabledSwitches] = useState([]);
  const [isExpanded, setIsExpanded] = useState([]);
  const [modifiedProperty, setModifiedProperty] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [focusedInputIndex, setFocusedInputIndex] = useState(null);
  const [modalId, setModalId] = useState(null);
  const [virtualTourLinks, setVirtualTourLinks] = useState(new Map());
  const [allSpecials, setAllAspecials] = useState([]);

  const axiosInstance = useAxiosInstance();

  useEffect(() => {
    getData();
  }, [currentUnit, onData, disabledSwitches, virtual]);

  useEffect(() => {
    generateVirtualTourLinks();
  }, [propertyDetails]);

  useEffect(() => {
    getAllSpecialsByPropertyId(propertyDetails?.id);
  }, [propertyDetails]);

  const generateVirtualTourLinks = () => {
    try {
      const newLinks = new Map();

      if (Array.isArray(propertyDetails?.virtual_tour)) {
        propertyDetails.virtual_tour?.forEach((x) => {
          if (x?.link) {
            const key = `${x?.name}-${x?.link}`;
            if (!newLinks.has(key)) {
              newLinks.set(key, { name: x?.name, link: x?.link });
            }
          }
        });
      }

      if (Array.isArray(driveData)) {
        driveData.forEach((tour) => {
          const marketName = propertyDetails?.market?.toLowerCase()?.trim();
          const propertyName = propertyDetails?.name?.toLowerCase()?.trim();

          if (
            tour?.Key?.toLowerCase()?.includes(marketName) &&
            tour?.Key?.toLowerCase()?.includes(propertyName + "/") &&
            !tour?.Key?.includes("-thumb-")
          ) {
            const generatedUrl =
              tour.Type === "folder"
                ? `${window.location.origin}/folder?folderUrl=${btoa(
                    encodeURIComponent(tour?.original)
                  )}`
                : tour?.Type === "document" || tour?.Type === "image"
                ? `${window.location.origin}/${tour?.Type}?documentUrl=${btoa(
                    encodeURIComponent(tour?.original)
                  )}`
                : `${window.location.origin}/video?videoUrl=${btoa(
                    encodeURIComponent(tour?.original)
                  )}`;

            const tourName = tour?.original?.split("/").pop();
            const key = `${tourName}-${generatedUrl}`;

            if (!newLinks.has(key)) {
              newLinks.set(key, { name: tourName, link: generatedUrl });
            }
          }
        });
      }
      setVirtualTourLinks(Array.from(newLinks.values()));
    } catch (error) {
      console.error("Error generating virtual tour links:", error);
    }
  };

  const toggleExpand = (index) => {
    const newExpandState = [...isExpanded];
    newExpandState[index] = !newExpandState[index];
    setIsExpanded(newExpandState);
  };

  const updateFloorPlanVideo = async (x) => {
    const response = await axiosInstance
      .put(`${process.env.REACT_APP_API_URL}/update-unit-virtual`, {
        data: currentVirtualModal,
        link: x?.link || "",
        name: x?.name || "",
      })
      .then((res) => {
        let updateLink = modifiedProperty;
        
        const {sameLink} = res.data
        if(sameLink){
           for (let item of updateLink?.models) {
          if (item?.model_name == currentVirtualModal.model_name) {
            item.floor_plan_video = "";
            item.floor_plan_video_name ="";
            console.log({current})
            virtual(item?.model_name, current);
            updateVirtualTourLink(
              currentVirtualModal,
               "",
               ""
            );
          }
        }
        return
        }
        

        for (let item of updateLink?.models) {
          if (item?.model_name == currentVirtualModal.model_name) {
            item.floor_plan_video = x?.link || "";
            item.floor_plan_video_name = x?.name || "";
            virtual(item?.model_name, current);
            updateVirtualTourLink(
              currentVirtualModal,
              x?.link || "",
              x?.name || ""
            );
          }
        }
        setModifiedProperty({});
        setModifiedProperty(updateLink);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleBlur = (index, id) => () => {
    setEditMode(!editMode);
    setFocusedInputIndex(null);
    setModalId(null);
  };

  const handleBlurForNewSpecial = (index, id) => () => {
    setEditModeForNewSpecial(!editModeForNewSpecial);
    setFocusedInputIndex(null);
    setModalId(null);
  };

  const handleFocus = (index, id) => () => {
    setEditMode(!editMode);
    setFocusedInputIndex(index);
    setModalId(id);
  };

  const handleFocusForNewSpecial = (index, id) => () => {
    setEditModeForNewSpecial(!editModeForNewSpecial);
    setFocusedInputIndex(index);
    setModalId(id);
  };

  const getData = () => {
    setModifiedProperty({});
    let data = {
      models: [],
    };
    let virtualTour = "";
    if (propertyDetails?.virtual_tour?.length > 0) {
      for (let i = 0; i < propertyDetails.virtual_tour?.length; i++) {
        virtualTour = virtualTour + propertyDetails.virtual_tour[i].link + " ";
      }
    }
    data.property_name = propertyDetails?.name;
    data.address =
      (propertyDetails?.street_address == "" ||
      propertyDetails?.street_address == null
        ? ""
        : propertyDetails?.street_address + ", ") +
      (propertyDetails?.city == "" || propertyDetails?.city == null
        ? ""
        : propertyDetails?.city + ", ") +
      (propertyDetails?.state == "" || propertyDetails?.state == null
        ? ""
        : propertyDetails?.state + ", ") +
      (propertyDetails?.zip_code == "" || propertyDetails?.zip_code == null
        ? ""
        : propertyDetails?.zip_code);
    data.website = propertyDetails?.website;
    data.google_maps_url = propertyDetails?.google_maps_url;
    data.virtual_tour = virtualTour;
    data.phone = propertyDetails?.phone;
    data.email =
      propertyDetails?.email_1 != null && propertyDetails?.email_1 != ""
        ? propertyDetails?.email_1
        : propertyDetails?.email_2 != null && propertyDetails?.email_2 != ""
        ? propertyDetails?.email_2
        : propertyDetails?.email_3 != null && propertyDetails?.email_3 != ""
        ? propertyDetails?.email_3
        : propertyDetails?.email_4;
    data.year_built = propertyDetails?.year_built?.toString();
    data.year_renovated = propertyDetails?.year_renovated?.toString();
    data.escort = propertyDetails?.escort?.toString();
    data.send = propertyDetails?.send?.toString();
    data.bonus = propertyDetails?.bonus?.toString();
    if (currentUnit?.length > 0) {
      let updatedProperty = currentUnit?.sort((a, b) => {
        if (a.model_name < b.model_name) {
          return -1;
        }
        if (a.model_name > b.model_name) {
          return 1;
        }
        return 0;
      });
      if (current == 0)
        updatedProperty = updatedProperty?.filter((x) => x.unit_studio == true);
      else if (current >= 1 && current <= 3)
        updatedProperty = updatedProperty?.filter(
          (x) => x.unit_bedroom == current
        );
      else if (current == 4)
        updatedProperty = updatedProperty?.filter(
          (x) => x.unit_bedroom >= current
        );
      if (updatedProperty?.length > 0) {
        data.units = updatedProperty[0].units_available;
        data.square_feet = updatedProperty[0].square_feet;

        while (updatedProperty?.length != 0) {
          var modelDetails = updatedProperty.filter(
            (x) => x.model_name == updatedProperty[0].model_name
          );
          propertyDetails["move_in_special"] = "";
          let propertyDetailsModel = [];
          propertyDetailsModel.push({
            price: "",
            unit: "",
            sqft: "",
            availabilityDate: "",
            availability: "",
            bedRoom: "",
            bathRoom: "",
            move_in_special: "",
            archived: "",
            id: "",
            special_toggle: "",
            special_calculation_price: "PRICE",
            special_calculation_text: "SPECIAL",
          });
          for (let i = 0; i < modelDetails?.length; i++) {
            onData(modelDetails[i]?.move_in_special);
            propertyDetailsModel.push({
              price: modelDetails[i].unit_price,
              rent: modelDetails[i].rent?.split("–")[0]?.trim(),
              unit: modelDetails[i].unit,
              sqft: modelDetails[i].unit_sq_ft,
              availabilityDate: modelDetails[i]?.unit_availability,
              availability: modelDetails[i]?.unit_availability_text,
              bedRoom: modelDetails[i]?.unit_bedroom,
              bathRoom: modelDetails[i]?.unit_bathroom,
              studio: modelDetails[i]?.unit_studio,
              move_in_special: modelDetails[i]?.move_in_special,
              archived: modelDetails[i]?.archived,
              id: modelDetails[i]?.id,
              special_toggle: modelDetails[i]?.special_toggle,
              special_calculation_price:
                modelDetails[i]?.special_calculation_price,
              special_calculation_text:
                modelDetails[i]?.special_calculation_text,
              rent_concession_reference:
                modelDetails[i]?.rent_concession_reference,
              months_fee_reference: modelDetails[i]?.months_fee_reference,
              yard: modelDetails[i]?.yard,
              garage: modelDetails[i]?.garage,
              features: modelDetails[i]?.features,
            });
            if (modelDetails[i]?.special_toggle == true) {
              let disable = disabledSwitches;
              disable.push(modelDetails[i]?.id);
              setDisabledSwitches(disable);
            }
          }

          propertyDetails["models"] = [];
          data.models.push({
            floorplan_link: updatedProperty[0].floor_plan,
            model_name: updatedProperty[0].model_name,
            monthly_rent: updatedProperty[0].monthly_rent,
            floor_plan: updatedProperty[0].floor_plan,
            floor_plan_video: updatedProperty[0]?.floor_plan_video,
            floor_plan_video_name: updatedProperty[0]?.floor_plan_video_name,
            property_id: propertyDetails.id,
            propertyDetails: propertyDetailsModel,
            details: updatedProperty[0]?.unit_details
              ?.replace("sq ft", "")
              ?.replace("sq. ft.", ""),
            lease_term: updatedProperty[0].lease_term,
          });
          updatedProperty = updatedProperty.filter(
            (unit) => unit.model_name !== updatedProperty[0].model_name
          );
        }
        data.models.forEach((model) => {
          model.propertyDetails.sort((a, b) => a.price - b.price);
        });
        setModifiedProperty(data);
      }
    } else {
      let updatedProperty = propertyDetails?.units?.sort((a, b) => {
        if (a.model_name < b.model_name) {
          return -1;
        }
        if (a.model_name > b.model_name) {
          return 1;
        }
        return 0;
      });
      if (current == 0)
        updatedProperty = updatedProperty?.filter((x) => x.unit_studio == true);
      else if (current >= 1 && current <= 3)
        updatedProperty = updatedProperty?.filter(
          (x) => x.unit_bedroom == current
        );
      else if (current == 4)
        updatedProperty = updatedProperty?.filter(
          (x) => x.unit_bedroom >= current
        );
      if (updatedProperty?.length > 0) {
        data.units = updatedProperty[0].units_available;
        data.square_feet = updatedProperty[0].square_feet;

        //data.details = updatedProperty[0].details;
        let model = "";
        while (updatedProperty?.length != 0) {
          var modelDetails = updatedProperty.filter(
            (x) => x.model_name == updatedProperty[0].model_name
          );
          propertyDetails["move_in_special"] = "";
          let propertyDetailsModel = [];
          propertyDetailsModel.push({
            price: "",
            unit: "",
            sqft: "",
            availabilityDate: "",
            availability: "",
            bedRoom: "",
            bathRoom: "",
            move_in_special: "",
            archived: "",
            id: "",
            special_toggle: "",
            special_calculation_price: "PRICE",
            special_calculation_text: "SPECIAL",
          });
          for (let i = 0; i < modelDetails?.length; i++) {
            onData(modelDetails[i]?.move_in_special);
            propertyDetailsModel.push({
              price: modelDetails[i].unit_price,
              rent: modelDetails[i].rent?.split("–")[0]?.trim(),
              unit: modelDetails[i].unit,
              sqft: modelDetails[i].unit_sq_ft,
              availabilityDate: modelDetails[i]?.unit_availability,
              availability: modelDetails[i]?.unit_availability_text,
              bedRoom: modelDetails[i]?.unit_bedroom,
              bathRoom: modelDetails[i]?.unit_bathroom,
              move_in_special: modelDetails[i]?.move_in_special,
              archived: modelDetails[i]?.archived,
              id: modelDetails[i]?.id,
              special_toggle: modelDetails[i]?.special_toggle,
              special_calculation_price:
                modelDetails[i]?.special_calculation_price,
              special_calculation_text:
                modelDetails[i]?.special_calculation_text,
              rent_concession_reference:
                modelDetails[i]?.rent_concession_reference,
              months_fee_reference: modelDetails[i]?.months_fee_reference,
              yard: modelDetails[i]?.yard,
              garage: modelDetails[i]?.garage,
              features: modelDetails[i]?.features
            });
            if (modelDetails[i]?.special_toggle == true) {
              let disable = disabledSwitches;
              disable.push(modelDetails[i]?.id);
              setDisabledSwitches(disable);
            }
          }
          propertyDetails["models"] = [];
          data.models.push({
            floorplan_link: updatedProperty[0].floor_plan,
            model_name: updatedProperty[0].model_name,
            monthly_rent: updatedProperty[0].monthly_rent,
            floor_plan: updatedProperty[0].floor_plan,
            floor_plan_video: updatedProperty[0]?.floor_plan_video,
            floor_plan_video_name: updatedProperty[0]?.floor_plan_video_name,
            property_id: propertyDetails.id,
            propertyDetails: propertyDetailsModel,
            details: updatedProperty[0].unit_details
              .replace("sq ft", "")
              .replace("sq. ft.", ""),
            lease_term: updatedProperty[0].lease_term,
          });
          updatedProperty = updatedProperty.filter(
            (unit) => unit.model_name !== updatedProperty[0].model_name
          );
        }
        data.models.forEach((model) => {
          model.propertyDetails.sort((a, b) => a.price - b.price);
        });
        setModifiedProperty(data);
      }
    }
    return () => {
      onData("");
    };
  };

  const handleClick = (event, details) => {
    setCurrentVirtualModal(details);
    setAnchorEl(event.currentTarget);
  };

  const copyUnit = (e, type, model) => {
    handleActionOneUnit(currentRow, e, type, model, setSnackbarOpen);
  };

  const copyUnitNewSpecial = (e, type, model, specialText, specialPrice) => {
    handleActionOneUnitNewSpecial(
      currentRow,
      e,
      type,
      model,
      specialText,
      specialPrice,
      setSnackbarOpen
    );
  };

  const applySpecialCalculation = (unit = {}, matchedSpecial = {}) => {
    try {
      const rent = parseFloat(unit?.price ?? unit?.unit_price ?? 0) || 0;
      const term = parseFloat(matchedSpecial?.lease_term ?? 0) || 0;
      const freeMonths = parseFloat(matchedSpecial?.months_free ?? 0) || 0;
      const concession =
        parseFloat(matchedSpecial?.custom_concession ?? 0) || 0;

      const totalRentWithMonthsFree = rent * (term - freeMonths);
      const totalRentWithBoth = totalRentWithMonthsFree - concession;
      const proratedWithBoth = totalRentWithBoth / term;

      // const specialPrice = !isFinite(proratedWithBoth)
      //   ? null
      //   : parseFloat(proratedWithBoth.toFixed(2));

      const specialPrice = !isFinite(proratedWithBoth)
        ? null
        : parseInt(proratedWithBoth);

      return specialPrice;
    } catch (err) {
      console.error("Error calculating special price:", err);
      return null;
    }
  };

  const getApplicableSpecialForUnit = (
    details = {},
    unit = {},
    specials = []
  ) => {
    try {
      const now = new Date();

      const unitNumber = String(unit?.unit ?? "").toLowerCase();
      const floorPlan = String(details?.model_name ?? "").toLowerCase();
      const bedCountRaw = String(unit?.bedRoom ?? "").trim();
      const bedCount = bedCountRaw === "0" ? "studio" : `${bedCountRaw} bed`;

      const moveInDate = String(unit?.availability ?? "")
        .toLowerCase()
        .trim();

      const isVacant = moveInDate === "now";
      const isNTV = moveInDate && moveInDate !== "now";

      // check the specials units have isVacant or isNTV logic -- start

      let checkMatchSpecial = specials.filter((val) => {
        if (!val.unit_types || val.unit_types.length === 0) {
          return true;
        }
        return val.unit_types.some(
          (type) =>
            type.toLowerCase() === bedCount.toLowerCase() ||
            type.toLowerCase() === "all units" || type.toLowerCase() === "all others" || type.toLowerCase() === "vacant" || type.toLowerCase() === "ntv"
        );
      });

      specials = checkMatchSpecial;

      if (checkMatchSpecial.length > 1) {
        const vacantType = isVacant ? "Vacant" : isNTV ? "NTV" : null;
        if (vacantType) {
          const MatchSpecial = specials.filter((val) => {
            return val.unit_types.some(
              (type) => type.toLowerCase() === vacantType.toLowerCase()
            );
          });
          checkMatchSpecial = MatchSpecial;
        }
      }
      if (
        checkMatchSpecial.length > 0 &&
        checkMatchSpecial[0].unit_types.length > 1
      ) {
        const checkUnitType = checkMatchSpecial[0].unit_types.map((t) =>
          t.toLowerCase()
        );

        if (checkUnitType.includes("vacant") && isVacant) {
          specials = checkMatchSpecial;
        } else if (checkUnitType.includes("ntv") && isNTV) {
          specials = checkMatchSpecial;
        } else {
          if(!checkUnitType.some((type)=> ["vacant", "ntv"].includes(type))){
            specials = checkMatchSpecial;
          }else{
            specials = null;
          }
        }
      }
      // check the specials units have isVacant or isNTV logic -- end

      const normalizeArray = (arr) =>
        (arr ?? []).map((x) => String(x).toLowerCase().trim());

      let activeSpecials = specials?.filter((special) => {
        const from = special?.date_from ? new Date(special?.date_from) : null;
        const to = special?.date_to ? new Date(special?.date_to) : null;
        return (!from || from <= now) && (!to || to >= now);
      });
      activeSpecials = processSpecials(activeSpecials, specialOrder);
      return (
        activeSpecials.find((s) =>
          normalizeArray(s?.unit_numbers)?.includes(unitNumber)
        ) ||
        activeSpecials.find((s) =>
          normalizeArray(s?.floor_plans)?.includes(floorPlan)
        ) ||
        activeSpecials.find((s) => {
          const unitTypes = normalizeArray(s?.unit_types);
          const standardTypes = ["1 bed", "2 bed", "3 bed", "4 bed", "studio"];

          const isStandardType = standardTypes.includes(bedCount);
          const isAllOthers = !isStandardType;

          return (
            (unitTypes.includes("studio") && unit?.studio) ||
            unitTypes.includes(bedCount) ||
            unitTypes.includes("all units") ||
            unitTypes.includes("all") ||
            (unitTypes.includes("vacant") && isVacant) ||
            (unitTypes.includes("ntv") && isNTV) ||
            unitTypes.includes("all others")
          );
        }) ||
        null
      );
    } catch (err) {
      console.error("Error finding applicable special:", err);
      return null;
    }
  };

  const getAllSpecialsByPropertyId = async (propertyId) => {
    try {
      await axiosInstance
        .get(`${process.env.REACT_APP_API_URL}/specials/${propertyId}`)
        .then((res) => {
          const specialsData = res?.data?.data || [];

          setAllAspecials(specialsData);
        })
        .catch((error) => {
          setAllAspecials([]);
          console.error("Error fetching specials:", error);
        });
    } catch (error) {
      console.error("Unexpected error:", error);
      setAllAspecials([]);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const addToLink = (unit_id = "") => {
    if (!unit_id || !selectedClient) return;
    axiosInstance
      .post(
        `${process.env.REACT_APP_API_URL}/shared-links/${selectedClient}/units`,
        { unit_id }
      )
      .then((response) => response.data)
      .then((result) => {
        if (result?.data && result?.success) {
          messageApi.success(`${result?.message || "Success!"}`);
        } else if (result?.data && !result?.success) {
          messageApi.info(`${result?.message || "Success!"}`);
        }
      })
      .catch((err) => {
        console.error(err);
        messageApi.error(`${err?.message || "Failed!"}`);
      });
  };

  const open = Boolean(anchorEl);

  const editUnit = (property, unit) => {
    const unitData = {
      unitId: unit?.id,
      property_id: property?.property_id,
      unit: unit?.unit,
      unit_bedroom: unit?.bedRoom,
      unit_bathroom: unit?.bathRoom,
      unit_price: unit?.price,
      unit_sq_ft: unit?.sqft,
      model_name: property?.model_name,
      monthly_rent: property?.monthly_rent,
      unit_availability: unit?.availabilityDate?.split("T")[0],
      floor_plan: property?.floor_plan,
      floor_plan_video: property?.floor_plan_video,
    };
    handleUpdateUnit(unitData);
  };

  const formatAvailability = (availability) => {
  if (!availability) return "";

  return availability.replace(/,\s*\d{4}/, "");
};

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        position: "relative",
        bottom: "25px",
      }}
    >
      <Snackbar
        open={snackbarOpen}
        style={{ right: 0, top: "65px" }}
        autoHideDuration={1000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={handleCloseSnackbar}
        message="Copied to clipboard!"
      />
      {modifiedProperty?.models?.length ? (
        <span
          style={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
          }}
        >
          {modifiedProperty?.models?.map((details, i) => (
            <div style={{ marginBottom: "15px", width: "50%" }}>
              <div
                style={{
                  boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
                  margin: "10px",
                  height: "100%",
                  borderRadius: "10px",
                }}
              >
                <div style={{ margin: "10px" }}>
                  <div
                    style={{
                      borderBottom: "1px solid #cfcfcf",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "800",
                          color: "#67bba9",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginRight: "20px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "end",
                            }}
                          >
                            {details.model_name}
                          </div>
                          <div style={{ display: "flex" }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
                                borderRadius: "50%",

                                marginTop: "17px",
                              }}
                            ></div>
                            <div
                              style={{
                                marginTop: "17px",
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: "15px",
                          fontWeight: "500",
                        }}
                      >
                        {details.monthly_rent?.split("-")[0]?.trim()}
                      </div>
                      <div
                        style={{
                          marginBottom: "20px",
                          color: "#adb4c1",
                        }}
                      >
                        {details.details}sf
                        {details.lease_term}
                      </div>
                    </div>
                    <div>
                      <div style={{ display: "flex", flexDirection: "row" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            flexDirection: "column",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <IconButton
                              color="primary"
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                handleClick(e, details);
                              }}
                            >
                              <VideoCallIcon />
                            </IconButton>
                            &nbsp;&nbsp;
                            <FavoriteBorderIcon
                              disabled
                              color={
                                (details.floor_plan_video != null &&
                                  details.floor_plan_video != "") ||
                                localLink[current].includes(details.model_name)
                                  ? "primary"
                                  : ""
                              }
                              style={{
                                cursor:
                                  (details.floor_plan_video != null &&
                                    details.floor_plan_video != "") ||
                                  localLink[current].includes(
                                    details.model_name
                                  )
                                    ? "pointer"
                                    : "not-allowed",
                              }}
                              onClick={() => {
                                if (
                                  (details.floor_plan_video != null &&
                                    details.floor_plan_video != "") ||
                                  localLink[current].includes(
                                    details.model_name
                                  )
                                ) {
                                  navigator.clipboard
                                    .writeText(details.floor_plan_video)
                                    .then(() => {
                                      console.log(
                                        "Virtual tour link copied to clipboard:",
                                        details.floor_plan_video != null &&
                                          details.floor_plan_video != ""
                                          ? details.floor_plan_video
                                          : localLink[current].includes(
                                              details.model_name
                                            )
                                      );
                                      setSnackbarOpen(true);
                                    })
                                    .catch((error) => {
                                      console.error(
                                        "Failed to copy virtual tour link:",
                                        error
                                      );
                                    });
                                }
                              }}
                            />
                          </div>
                          <div>
                            {(() => {
                              const virtualTourMatch =
                                propertyDetails?.virtual_tour?.find(
                                  (x) =>
                                    x?.link === details?.floor_plan_video &&
                                    x?.name === details?.floor_plan_video_name
                                );

                              if (virtualTourMatch) {
                                return (
                                  <div key={details?.floor_plan_video}>
                                    {virtualTourMatch?.name} 
                                  </div>
                                );
                              }

                              const tour = driveData
                                ?.filter(
                                  (x) =>
                                    x?.Key?.includes(
                                      propertyDetails?.market?.toLowerCase()
                                    ) &&
                                    x?.Key?.includes(
                                      propertyDetails?.name?.toLowerCase() + "/"
                                    ) &&
                                    !x?.Key?.includes("-thumb-")
                                )
                                ?.find((tour) => {
                                  const url =
                                    tour.Type === "folder"
                                      ? `${
                                          window.location.origin
                                        }/folder?folderUrl=${btoa(
                                          encodeURIComponent(tour?.original)
                                        )}`
                                      : tour.Type === "document" ||
                                        tour.Type === "image"
                                      ? `${window.location.origin}/${
                                          tour.Type
                                        }?documentUrl=${btoa(
                                          encodeURIComponent(tour?.original)
                                        )}`
                                      : `${
                                          window.location.origin
                                        }/video?videoUrl=${btoa(
                                          encodeURIComponent(tour?.original)
                                        )}`;

                                  return (
                                    url === details?.floor_plan_video || ""
                                  );
                                });

                              if (tour) {
                                const fileName = tour?.original
                                  ?.split("/")
                                  .pop();
                                  console.log({fileName})
                                return <div>{fileName}</div>;
                              }


                              return details?.floor_plan_video_name || null;
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div style={{ padding: "10px", display: "flex" }}>
                      <Image
                        height={70}
                        src={details.floor_plan}
                        placeholder={
                          <Image preview={false} src={details.floor_plan} />
                        }
                        preview={{
                          mask: <div>Preview</div>,
                          zIndex: 2000,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div key={i}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          fontSize: "15px",
                          fontWeight: "900",
                        }}
                      >
                        <button
                          onClick={() => toggleExpand(i)}
                          style={{ color: "#4e5c76" }}
                        >
                          <span>
                            {isExpanded[i]
                              ? `SHOW ${
                                  details?.propertyDetails?.length - 1
                                } PRICES`
                              : `HIDE ${
                                  details?.propertyDetails?.length - 1
                                } PRICES`}
                          </span>
                          {isExpanded[i] ? (
                            <KeyboardArrowDownIcon />
                          ) : (
                            <KeyboardArrowUpIcon />
                          )}
                        </button>
                      </div>
                      {!isExpanded[i] && (
                        
                        <div>
                          <div className="w-full overflow-x-auto xl:overflow-x-visible">
                            <div
                              className="
                                grid
                                grid-cols-[60px_60px_60px_230px_71px_50px_50px_70px]
                                min-w-[680px]
                                xl:min-w-0
                                xl:grid-cols-[60px_60px_60px_1fr_71px_50px_50px_70px]
                                items-center
                                h-9
                                bg-gray-100
                                border-b
                                font-semibold
                                text-sm
                              "
                            >
                              <div>Mkt $</div>
                              <div>Unit #</div>
                              <div>SF</div>
                              <div className="hidden xl:block min-w-0 truncate">Special</div>
                              <div className="truncate">Spcl $</div>
                              <div className="min-w-0 hidden xl:block">Amen</div>
                              <div className="hidden"></div>
                              <div className="hidden"></div>
                              <div></div>
                              <div className="sticky right-0 text-sm bg-gray-100 z-10 shrink-0">Avail</div>
                            </div>
                          </div>
                          {details?.propertyDetails?.map((amenties, index) => {
                            if (!amenties?.id) return <></>;
                            const matchedSpecial = getApplicableSpecialForUnit(
                              details,
                              amenties,
                              allSpecials
                            );
                            const specialPrice = matchedSpecial
                              ? applySpecialCalculation(
                                  amenties,
                                  matchedSpecial
                                )
                              : null;

                            const specialText = formatSpecialText(
                              matchedSpecial,
                              specialPrice
                            );
                           
                        
                            const formattedPrice = `${specialPrice?.toLocaleString()}`;
                            const specialTextNew = specialText.split("//")[0].trim();

                            return (
                              // the below div is non responsive 
                              // <div
                              //   style={{
                              //     borderBottom: "1px solid #cfcfcf",
                              //     color:
                              //       amenties?.archived == true ? "#adb4c1" : "",
                              //   }}
                              // >
                              //   <div
                              //     style={{
                              //       display: "flex",
                              //       flexDirection: "row",
                              //       width: "100%",
                              //       marginTop: "10px",
                              //       marginBottom: "10px",
                              //     }}
                              //   >
                              //     <div
                              //       style={{
                              //         width: "8%",
                              //         textAlign: "flex-start",
                              //       }}
                              //     >
                              //       {amenties?.price != 0
                              //         ? amenties?.price?.toLocaleString(
                              //             "en-US",
                              //             {
                              //               style: "currency",
                              //               currency: "USD",
                              //               minimumFractionDigits: 0,
                              //               maximumFractionDigits: 0,
                              //             }
                              //           )
                              //         : amenties?.rent?.split("-")[0]}
                              //     </div>
                              //     <div
                              //       style={{
                              //         // width: "3%",
                              //         textAlign: "flex-start",
                              //       }}
                              //     >
                              //       {amenties?.special_calculation_price ==
                              //       "PRICE" ? null : (
                              //         <>&nbsp;-&nbsp;</>
                              //       )}
                              //     </div>
                              //     <div
                              //       style={{
                              //         // width: "8%",
                              //         textAlign: "flex-start",
                              //       }}
                              //     >
                              //       {amenties.unit}
                              //     </div>
                              //     <div
                              //       style={{
                              //         // width: "3%",
                              //         textAlign: "flex-start",
                              //       }}
                              //     >
                              //       {amenties?.special_calculation_price ==
                              //       "PRICE" ? null : (
                              //         <>&nbsp;-&nbsp;</>
                              //       )}
                              //     </div>
                              //     <div
                              //       style={{
                              //         // width: "11%",
                              //         textAlign: "flex-start",
                              //       }}
                              //     >
                              //       {amenties?.special_calculation_price ==
                              //       "PRICE" ? null : (
                              //         <>
                              //           {amenties.sqft}sf
                                        
                              //         </>
                              //       )}
                              //     </div>
                              //     <div
                              //       style={{
                              //         width: "3%",
                              //         textAlign: "flex-start",
                              //       }}
                              //     >
                              //       {amenties?.special_calculation_price ==
                              //       "PRICE" ? null : (
                              //         <></>
                              //       )}
                              //     </div>
                              //     {/* <div
                              //       style={{
                              //         width: "12%",
                              //         textAlign: "flex-start",
                              //       }}
                              //     >
                              //       {amenties?.special_calculation_price ==
                              //       "PRICE" ? (
                              //         <span
                              //           style={{
                              //             color: "#4e5c76",
                              //             fontWeight: 800,
                              //             fontSize: "15px",
                              //           }}
                              //         >
                              //           <span style={{ fontWeight: 900 }}>
                              //             {amenties.special_calculation_price}
                              //           </span>
                              //         </span>
                              //       ) : (
                              //         <>
                              //           <span style={{ fontWeight: 900 }}>
                              //             <div className="flex flex-col justify-center items-center">
                              //               <div>
                              //                  <AmenitiesToggle amenities={amenties?.features?.split(", ")?.filter(f=>f?.length)}></AmenitiesToggle>
                              //               </div>
                              //               <div className="border-none">
                              //                 {matchedSpecial ? (
                              //                   <>{specialPrice}</>
                              //                 ) : (
                              //                   "No special"
                              //                 )}
                              //               </div>
                              //             </div>
                              //           </span>
                              //         </>
                              //       )}
                              //     </div> */}
                              //     <div
                              //       style={{
                              //         width: "35%",
                              //         textAlign: "flex-start",
                              //         whiteSpace: "nowrap",
                              //         overflow: "hidden",
                              //         textOverflow: "ellipsis",
                              //       }}
                              //     >
                              //       {amenties?.special_calculation_price ==
                              //       "PRICE" ? (
                              //         <span
                              //           style={{
                              //             color: "#4e5c76",
                              //             fontWeight: 800,
                              //             fontSize: "15px",
                              //           }}
                              //         >
                              //           {amenties.special_calculation_text}
                              //         </span>
                              //       ) : (
                              //         <>
                              //           {editMode &&
                              //             focusedInputIndex === index &&
                              //             modalId == amenties.id && (
                              //               <span>
                              //                 <input
                              //                   type="text"
                              //                   value={
                              //                     amenties.special_calculation_text
                              //                   }
                              //                   onBlur={handleBlur(
                              //                     index,
                              //                     amenties.id
                              //                   )}
                              //                   placeholder={`Input ${
                              //                     index + 1
                              //                   }`}
                              //                   style={{
                              //                     border: "2px solid #1976d2",
                              //                     borderRadius: "5px",
                              //                     width: "100%",
                              //                   }}
                              //                 />
                              //               </span>
                              //             )}
                              //           {editModeForNewSpecial &&
                              //             focusedInputIndex === index &&
                              //             modalId == amenties.id && (
                              //               <span>
                              //                 <input
                              //                   type="text"
                              //                   value={specialText}
                              //                   onBlur={handleBlurForNewSpecial(
                              //                     index,
                              //                     amenties.id
                              //                   )}
                              //                   placeholder={`Input ${
                              //                     index + 1
                              //                   }`}
                              //                   style={{
                              //                     border: "2px solid #1976d2",
                              //                     borderRadius: "5px",
                              //                     width: "100%",
                              //                   }}
                              //                 />
                              //               </span>
                              //             )}
                              //           {modalId != amenties.id && (
                              //             <div className="flex flex-col">
                              //               <span
                              //                 onClick={handleFocus(
                              //                   index,
                              //                   amenties.id
                              //                 )}
                              //               >
                              //                 {
                              //                   amenties.special_calculation_text
                              //                 }
                              //               </span>
                              //               {/* <span
                              //                 onClick={handleFocusForNewSpecial(
                              //                   index,
                              //                   amenties.id
                              //                 )}
                              //                 className="mt-5 border-t-2"
                              //               >
                              //                 {specialText || ""}
                              //               </span> */}
                              //               {specialText || ""}
                              //             </div>
                              //           )}
                              //         </>
                              //       )}
                              //     </div>
                              //     <>&nbsp;-&nbsp;</>
                              //      <div className="border-none" style={{ fontWeight: 900 }}>
                              //                 {matchedSpecial ? (
                              //                   <>{specialPrice}</>
                              //                 ) : (
                              //                   "No special"
                              //                 )}
                              //               </div>
                              //      <div
                              //       style={{
                              //         width: "12%",
                              //         textAlign: "flex-start",
                              //       }}
                              //     >
                              //       {amenties?.special_calculation_price ==
                              //       "PRICE" ? (
                              //         <span
                              //           style={{
                              //             color: "#4e5c76",
                              //             fontWeight: 800,
                              //             fontSize: "15px",
                              //           }}
                              //         >
                              //           <span style={{ fontWeight: 900 }}>
                              //             {amenties.special_calculation_price}
                              //           </span>
                              //         </span>
                              //       ) : (
                              //         <>
                              //           <span style={{ fontWeight: 900 }}>
                              //             <div className="flex flex-col justify-center items-center">
                              //               <div>
                              //                  <AmenitiesToggle amenities={amenties?.features?.split(", ")?.filter(f=>f?.length)}></AmenitiesToggle>
                              //               </div>
                              //               {/* <div className="border-none">
                              //                 {matchedSpecial ? (
                              //                   <>{specialPrice}</>
                              //                 ) : (
                              //                   "No special"
                              //                 )}
                              //               </div> */}
                              //             </div>
                              //           </span>
                              //         </>
                              //       )}
                              //     </div>
                              //     <div
                              //       className="pl-5"
                              //       style={{
                              //         width: "6%",
                              //         textAlign: "flex-start",
                              //       }}
                              //     >
                              //       {formatAvailability(amenties.availability)}
                              //     </div>
                              //     <div
                              //       style={{
                              //         width: "2%",
                              //         textAlign: "flex-start",
                              //         color: "green",
                              //         fontWeight: "bold",
                              //         textAlign: "center",
                              //         fontSize: "18px",
                              //       }}
                              //     >
                              //       {amenties.yard ? "Y" : ""}
                              //     </div>
                              //     <div
                              //       style={{
                              //         width: "2%",
                              //         textAlign: "flex-start",
                              //         color: "Blue",
                              //         fontWeight: "bold",
                              //         textAlign: "center",
                              //         fontSize: "18px",
                              //       }}
                              //     >
                              //       {amenties.garage ? "G" : ""}
                              //     </div>
                              //     <div
                              //       style={{
                              //         // width: "7%",
                              //         display: "flex",
                              //         justifyContent: "end",
                              //       }}
                              //     >
                              //       {/* Copy Units Heart Icon */}
                              //       {/* {amenties?.special_calculation_price ==
                              //       "PRICE" ? null : (
                              //         <div className="flex flex-col">
                              //           <IconButton>
                              //             <FavoriteBorderIcon
                              //               onClick={() => {
                              //                 copyUnit(
                              //                   amenties.unit +
                              //                     "_" +
                              //                     amenties.sqft +
                              //                     "_" +
                              //                     amenties.price +
                              //                     "_" +
                              //                     amenties.availability +
                              //                     "_" +
                              //                     amenties.bedRoom +
                              //                     "_" +
                              //                     amenties.bathRoom +
                              //                     "_" +
                              //                     amenties.move_in_special +
                              //                     "_" +
                              //                     (amenties.special_calculation_price !=
                              //                       null &&
                              //                     amenties.special_calculation_price !=
                              //                       ""
                              //                       ? parseInt(
                              //                           amenties.special_calculation_price
                              //                         )
                              //                       : ""),
                              //                   "sub",
                              //                   details.model_name
                              //                 );
                              //               }}
                              //             />
                              //           </IconButton>
                              //           <IconButton
                              //             style={{ color: "#FF69B4" }}
                              //           >
                              //             <FavoriteBorderIcon
                              //               color="#FF69B4"
                              //               onClick={() => {
                              //                 copyUnitNewSpecial(
                              //                   amenties.unit +
                              //                     "_" +
                              //                     amenties.sqft +
                              //                     "_" +
                              //                     amenties.price +
                              //                     "_" +
                              //                     amenties.availability +
                              //                     "_" +
                              //                     amenties.bedRoom +
                              //                     "_" +
                              //                     amenties.bathRoom +
                              //                     "_" +
                              //                     amenties.move_in_special +
                              //                     "_" +
                              //                     (amenties.special_calculation_price !=
                              //                       null &&
                              //                     amenties.special_calculation_price !=
                              //                       ""
                              //                       ? parseInt(
                              //                           amenties.special_calculation_price
                              //                         )
                              //                       : ""),
                              //                   "sub",
                              //                   details.model_name,
                              //                   specialText,
                              //                   specialPrice
                              //                 );
                              //               }}
                              //             />
                              //           </IconButton>
                              //         </div>
                              //       )} */}
                              //       <div
                              //         style={{
                              //           display: "flex",
                              //           flexDirection: "column",
                              //         }}
                              //       >
                              //         {amenties?.special_calculation_price ==
                              //         "PRICE" ? null : selectedClient ? (
                              //           <Tooltip title="Add to client's shared link">
                              //             <IconButton
                              //               onClick={() =>
                              //                 addToLink(amenties?.id)
                              //               }
                              //               color="primary"
                              //             >
                              //               <DomainAddIcon />
                              //             </IconButton>
                              //           </Tooltip>
                              //         ) : null}
                              //         {!propertyDetails.apartment_url && (
                              //           <Tooltip title="Edit Unit">
                              //             <IconButton
                              //               color="primary"
                              //               style={{ cursor: "pointer" }}
                              //               onClick={() => {
                              //                 editUnit(details, amenties);
                              //               }}
                              //             >
                              //               <ModeIcon />
                              //             </IconButton>
                              //           </Tooltip>
                              //         )}
                              //       </div>
                              //     </div>
                              //   </div>
                              // </div>
                              <div className="border-b border-gray-300" style={{ color: amenties?.archived == true ? "#adb4c1" : "" }}>
  {/* <div className="flex flex-row w-full my-2.5 flex-wrap gap-y-1"> */}
                                <div
                                  className=""
                                >
                                  {/* Scroll container - Mobile + Tablet */}
                                  <div className="w-full overflow-x-auto xl:overflow-x-visible">
                                    {/* Actual table width */}
                                    <div
                                      className="
                                        grid
                                        grid-cols-[60px_60px_60px_230px_71px_50px_50px_70px]
                                        min-w-[680px]
                                        xl:min-w-0
                                        xl:grid-cols-[60px_60px_60px_1fr_71px_50px_50px_70px]
                                        items-center
                                        min-h-[50px]
                                      "
                                    >

                                      {/* Price */}
                                      <div className="text-sm">
                                        {amenties?.price != 0
                                          ? amenties?.price?.toLocaleString("en-US", {
                                            style: "currency",
                                            currency: "USD",
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 0,
                                          })
                                          : amenties?.rent?.split("-")[0]}
                                      </div>

                                      {/* Unit */}
                                      <div className="text-sm">
                                        {amenties?.unit}
                                      </div>

                                      {/* SF */}
                                      <div className="text-sm">
                                        {amenties?.special_calculation_price === "PRICE"
                                          ? null
                                          : `${amenties?.sqft}sf`}
                                      </div>

                                      {/* Special Calculation Text */}
                                      <div className="text-sm min-w-0 truncate px-1 hidden xl:block mr-1">
                                        {amenties?.special_calculation_price === "PRICE" ? (
                                          <span className="text-[#4e5c76] font-extrabold text-sm truncate block">
                                            {amenties?.special_calculation_text}
                                          </span>
                                        ) : (
                                          <>
                                            {editMode &&
                                              focusedInputIndex === index &&
                                              modalId === amenties.id && (
                                                <input
                                                  type="text"
                                                  value={amenties?.special_calculation_text || ""}
                                                  onBlur={handleBlur(index, amenties.id)}
                                                  placeholder={`Input ${index + 1}`}
                                                  className="
                                                    border-2
                                                    border-[#1976d2]
                                                    rounded-[5px]
                                                    w-full
                                                    min-w-0
                                                  "
                                                />
                                              )}

                                            {editModeForNewSpecial &&
                                              focusedInputIndex === index &&
                                              modalId === amenties.id && (
                                                <input
                                                  type="text"
                                                  value={specialTextNew}
                                                  onBlur={handleBlurForNewSpecial(index, amenties.id)}
                                                  placeholder={`Input ${index + 1}`}
                                                  className="
                                                    border-2
                                                    border-[#1976d2]
                                                    rounded-[5px]
                                                    w-full
                                                    min-w-0
                                                  "
                                                />
                                              )}

                                            {modalId !== amenties.id && (
                                              <div className="min-w-0">
                                                <span
                                                  onClick={handleFocus(index, amenties.id)}
                                                  className="truncate block cursor-pointer"
                                                >
                                                  {amenties?.special_calculation_text}
                                                </span>

                                                {specialTextNew || ""}
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </div>

                                      {/* Special Price */}
                                      <div className="font-black text-sm">
                                        {matchedSpecial ? `$${formattedPrice}` : "No special"}
                                      </div>

                                      {/* Amenities */}
                                      <div className="text-sm truncate min-w-0 hidden xl:block">
                                        {amenties?.special_calculation_price === "PRICE" ? (
                                          <span className="text-[#4e5c76] font-extrabold">
                                            {amenties?.special_calculation_price}
                                          </span>
                                        ) : (
                                          <AmenitiesToggle
                                            amenities={amenties?.features
                                              ?.split(", ")
                                              ?.filter((f) => f?.length)}
                                          />
                                        )}
                                      </div>

                                      {/* Yard */}
                                      <div className="text-center text-lg font-bold text-green-600 w-[20px] hidden">
                                        {amenties?.yard ? "Y" : ""}
                                      </div>

                                      {/* Garage */}
                                      <div className="text-center text-lg font-bold text-blue-600 w-[20px] hidden">
                                        {amenties?.garage ? "G" : ""}
                                      </div>

                                      {/* Actions */}
                                      <div className="flex flex-col items-center">
                                      {amenties?.special_calculation_price == "PRICE" ? null : selectedClient ? (

                                        <Tooltip title="Add to client's shared link">
                                          <IconButton
                                            onClick={() => addToLink(amenties?.id)}
                                            color="primary"
                                            size="small"
                                          >
                                            <DomainAddIcon />
                                          </IconButton>
                                        </Tooltip>
                                        ) : null}

                                        {!propertyDetails.apartment_url && (

                                        <Tooltip title="Edit Unit">
                                          <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={() => editUnit(details, amenties)}
                                          >
                                            <ModeIcon />
                                          </IconButton>
                                        </Tooltip>
                                        )}

                                      </div>

                                      {/* Availability */}
                                      <div className="text-sm sticky right-0 text-sm bg-white z-10 shrink-0">
                                        {formatAvailability(amenties?.availability)}
                                      </div>

                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </span>
      ) : (
        <div style={{ marginLeft: "10px" }}>No Record Found</div>
      )}
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        {virtualTourLinks && virtualTourLinks?.length > 0
          ? virtualTourLinks?.map((item) => (
              <MenuItem
                key={item?.link}
                className="line-clamp-1"
                style={{
                  color:
                    currentVirtualModal?.floor_plan_video === item?.link &&
                    currentVirtualModal?.floor_plan_video_name === item?.name
                      ? "#1976D2"
                      : "",
                }}
                onClick={() => {
                  setAnchorEl(null);
                  updateFloorPlanVideo({ link: item?.link, name: item?.name });
                }}
              >
                {item?.name}
              </MenuItem>
            ))
          : null}
      </Menu>

      {contextHolder}
    </div>
  );
};
export default TabProperties;
